'use client';

import { FC, useContext, useState } from 'react';
import { ManageGroupsProps } from './ManageGroups.types';
import {
  Button,
  ButtonGroup,
  Dialog,
  Flex,
  Icon,
  Input,
  Text,
} from '@/_components';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { AddFirmGroup } from './components/AddFirmGroup';
import { EmptyFirmGroups } from './components/EmptyFirmGroups';
import {
  createFirmGroup,
  updateFirmGroup,
  deleteFirmGroup,
} from '@/_api/admin-firm-groups';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { DialogFooter } from '@/_components/Dialog/_components/DialogFooter';
import styles from './ManageGroups.module.css';

export const ManageGroups: FC<ManageGroupsProps> = ({ isOpen, onClose }) => {
  const { firm, firmGroups, reloadFirm } = useContext(FirmSettingsContext);
  const { token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);

  const [isLoading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [editingFirmGroupId, setEditingFirmGroupId] = useState<
    number | undefined
  >(undefined);
  const [isAdding, setAdding] = useState<boolean>(false);

  const hasFirmGroups: boolean = !!firm?.firm_groups?.length;
  const filteredFirmGroups = firmGroups?.filter((fg) =>
    fg.name.toLowerCase().includes(search.toLowerCase()),
  );

  const onToggleCreateGroup = () => {
    if (!isAdding) setEditingFirmGroupId(undefined);

    setAdding(!isAdding);
  };

  const onAddFirmGroup = async (name: string): Promise<void> => {
    try {
      if (editingFirmGroupId) {
        await updateFirmGroup({
          token,
          new_group_name: name,
          firm_group_id: editingFirmGroupId,
        });
      } else {
        await createFirmGroup({ token, new_group_name: name });
      }

      await reloadFirm();
    } catch (error) {
      console.error(error);
    } finally {
      setTimeout(() => setAdding(false), 500);
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Manage Firm Groups">
      <Dialog.Content>
        {isAdding && (
          <AddFirmGroup
            firmGroupId={editingFirmGroupId}
            onCancel={onToggleCreateGroup}
            onAddFirmGroup={onAddFirmGroup}
          />
        )}
        {!hasFirmGroups && (
          <EmptyFirmGroups onCreateFirmGroup={onToggleCreateGroup} />
        )}
        {!isAdding && hasFirmGroups && (
          <Flex isColumn={true} gap={12}>
            <Flex>
              <Input
                label="Search for firm group"
                placeholder="Search for firm group"
                isLabelHidden={true}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                iconLeft="search"
                iconRight={!!search ? 'x' : undefined}
                onRightClick={() => setSearch('')}
              />
            </Flex>
            <Flex
              isColumn={true}
              gap={4}
              className={styles.manageGroupsContainer}
            >
              {filteredFirmGroups?.map((fg, key) => {
                const onEdit = () => {
                  setEditingFirmGroupId(fg.id);
                  setAdding(true);
                };

                const onDelete = async () => {
                  try {
                    setLoading(true);
                    const { error } = await deleteFirmGroup({
                      token,
                      firm_group_id: fg.id,
                    });

                    if (error) addToast('error', error, { error });
                  } catch (error) {
                    console.error(error);
                  } finally {
                    setLoading(false);
                    reloadFirm();
                  }
                };

                return (
                  <Flex isYCentered={true} key={key} isBetween={true} gap={4}>
                    <Flex isYCentered={true} gap={4}>
                      <Icon name="tag" size={24} />
                      <Text>{fg.name}</Text>
                      <Text type="small">({fg.user_count} users)</Text>
                    </Flex>
                    <ButtonGroup isSameLine={true} isSmallGroup={true}>
                      <Button
                        intent="ghost"
                        value="Edit"
                        iconLeft="pencil"
                        size="small"
                        isIconOnly={true}
                        withTooltip={true}
                        isDisabled={isLoading}
                        onClick={onEdit}
                      />
                      <Button
                        intent="danger-ghost"
                        value="Delete"
                        iconLeft="trash"
                        isIconOnly={true}
                        withTooltip={true}
                        isDisabled={isLoading}
                        size="small"
                        onClick={onDelete}
                      />
                    </ButtonGroup>
                  </Flex>
                );
              })}
            </Flex>
          </Flex>
        )}
      </Dialog.Content>
      {!isAdding && hasFirmGroups && (
        <DialogFooter>
          <ButtonGroup align="right">
            <Button
              value="New Firm Group"
              intent="primary"
              size="normal"
              isDisabled={isLoading}
              iconLeft="plus"
              onClick={onToggleCreateGroup}
            />
          </ButtonGroup>
        </DialogFooter>
      )}
    </Dialog>
  );
};
